<template>
  <div
    class="relative group"
    :class="containerClass"
  >
    <label
      :for="inputId"
      class="sr-only"
    >{{ placeholder }}</label>
    
    <!-- Search Icon (when showIcon is true) -->
    <MagnifyingGlassIcon 
      v-if="showIcon"
      class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500 group-focus-within:text-indigo-500 transition-colors" 
    />
    
    <!-- Search Input -->
    <input
      :id="inputId"
      :value="modelValue"
      type="text"
      :placeholder="placeholder"
      :class="inputClasses"
      :disabled="disabled"
      @input="handleInput"
    >
    
    <!-- Clear Button (when showClear is true and there's a value) -->
    <button
      v-if="showClear && modelValue"
      type="button"
      class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
      @click="clearSearch"
    >
      <XCircleIcon class="h-4 w-4" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { MagnifyingGlassIcon, XCircleIcon } from '@heroicons/vue/24/outline';

interface Props {
  modelValue: string;
  placeholder?: string;
  variant?: 'default' | 'modern' | 'simple';
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  showClear?: boolean;
  disabled?: boolean;
  containerClass?: string;
  inputId?: string;
}

interface Emits {
  (e: 'update:modelValue', value: string): void;
  (e: 'search', value: string): void;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Buscar...',
  variant: 'modern',
  size: 'md',
  showIcon: true,
  showClear: false,
  disabled: false,
  containerClass: '',
  inputId: 'search-input'
});

const emit = defineEmits<Emits>();

const handleInput = (event: Event) => {
  const value = (event.target as HTMLInputElement).value;
  emit('update:modelValue', value);
  emit('search', value);
};

const clearSearch = () => {
  emit('update:modelValue', '');
  emit('search', '');
};

const inputClasses = computed(() => {
  const baseClasses = [
    'block w-full min-w-0 transition-all',
    'placeholder:text-gray-400 dark:placeholder:text-gray-500',
    'focus:outline-none',
    'py-1'
  ];

  // Size classes
  const sizeClasses = {
    sm: 'py-2 text-xs',
    md: 'py-2.5 sm:py-3 text-sm',
    lg: 'py-3.5 text-base'
  };

  // Variant classes
  const variantClasses = {
    // Modern style (used in categories views)
    modern: [
      'rounded-xl border border-gray-200 bg-white/90 shadow-sm',
      'hover:border-gray-300',
      'focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20',
      'dark:border-gray-700 dark:bg-gray-900/80 dark:text-gray-100',
      props.showIcon ? 'pl-10' : 'pl-3',
      props.showClear ? 'pr-10' : 'pr-3'
    ],
    // Default style (used in menu list)
    default: [
      'rounded-md border border-gray-300',
      'focus:border-indigo-500 focus:ring-indigo-500',
      'dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100',
      props.showIcon ? 'pl-10' : 'pl-3',
      props.showClear ? 'pr-10' : 'pr-3'
    ],
    // Simple style (used in other places)
    simple: [
      'border border-gray-300 dark:border-gray-600 rounded-lg',
      'focus:ring-2 focus:ring-indigo-500',
      'dark:bg-gray-700 dark:text-white',
      'px-4 py-2'
    ]
  };

  // Disabled state
  const disabledClasses = props.disabled ? [
    'opacity-50 cursor-not-allowed',
    'dark:bg-gray-800 dark:text-gray-400'
  ] : [];

  return [
    ...baseClasses,
    ...sizeClasses[props.size],
    ...variantClasses[props.variant],
    ...disabledClasses
  ].filter(Boolean).join(' ');
});
</script>
